import type lang from '@/lang';
import type langSign from '@/langSign';
import type {State} from '@config/state';
import {IS_BETA, MOUNT_CLASS_TO} from '@config/debug';
import {HelpCountry, LangPackDifference, LangPackString} from '@layer';
import App from '@config/app';
import rootScope from '@lib/rootScope';
import {IS_MOBILE} from '@environment/userAgent';
import deepEqual from '@helpers/object/deepEqual';
import safeAssign from '@helpers/object/safeAssign';
import capitalizeFirstLetter from '@helpers/string/capitalizeFirstLetter';
import matchUrlProtocol from '@lib/richTextProcessor/matchUrlProtocol';
import wrapUrl from '@lib/richTextProcessor/wrapUrl';
import {setDirection} from '@helpers/dom/setInnerHTML';
import setBlankToAnchor from '@lib/richTextProcessor/setBlankToAnchor';
import {createSignal} from 'solid-js';
import commonStateStorage from '@lib/commonStateStorage';
import Icon from '@components/icon';
import currencyStarIcon from '@components/currencyStarIcon';

export const langPack: {[actionType: string]: LangPackKey} = {
  'messageActionChatCreate': 'ActionCreateGroup',
  'messageActionChatCreateYou': 'ActionYouCreateGroup',
  'messageActionChatEditTitle': 'ActionChangedTitle',
  'messageActionChatEditPhoto': 'ActionChangedPhoto',
  'messageActionChatEditVideo': 'ActionChangedVideo',
  'messageActionChatDeletePhoto': 'ActionRemovedPhoto',
  'messageActionChatReturn': 'ActionAddUserSelf',
  'messageActionChatReturnYou': 'ActionAddUserSelfYou',
  'messageActionChatJoined': 'ActionAddUserSelfMega',
  'messageActionChatJoinedYou': 'ChannelMegaJoined',
  'messageActionChatAddUser': 'ActionAddUser',
  'messageActionChatAddUsers': 'ActionAddUser',
  'messageActionChatLeave': 'ActionLeftUser',
  'messageActionChatLeaveYou': 'YouLeft',
  'messageActionChatDeleteUser': 'ActionKickUser',
  'messageActionChatJoinedByLink': 'ActionInviteUser',
  'messageActionPinMessage': 'Chat.Service.Group.UpdatedPinnedMessage',
  'messageActionContactSignUp': 'Chat.Service.PeerJoinedTelegram',
  'messageActionChannelCreate': 'ActionCreateChannel',
  'messageActionChannelEditTitle': 'Chat.Service.Channel.UpdatedTitle',
  'messageActionChannelEditPhoto': 'Chat.Service.Channel.UpdatedPhoto',
  'messageActionChannelEditVideo': 'Chat.Service.Channel.UpdatedVideo',
  'messageActionChannelDeletePhoto': 'Chat.Service.Channel.RemovedPhoto',
  'messageActionHistoryClear': 'HistoryCleared',
  'messageActionDiscussionStarted': 'DiscussionStarted',
  'messageActionChannelJoined': 'ChannelJoined',

  'messageActionChannelMigrateFrom': 'ActionMigrateFromGroup',

  'messageActionPhoneCall.video_in_ok': 'ChatList.Service.VideoCall.incoming',
  'messageActionPhoneCall.video_out_ok': 'ChatList.Service.VideoCall.outgoing',
  'messageActionPhoneCall.video_missed': 'ChatList.Service.VideoCall.Missed',
  'messageActionPhoneCall.video_cancelled': 'ChatList.Service.VideoCall.Cancelled',
  'messageActionPhoneCall.in_ok': 'ChatList.Service.Call.incoming',
  'messageActionPhoneCall.out_ok': 'ChatList.Service.Call.outgoing',
  'messageActionPhoneCall.missed': 'ChatList.Service.Call.Missed',
  'messageActionPhoneCall.cancelled': 'ChatList.Service.Call.Cancelled',

  'messageActionGroupCall.started': 'Chat.Service.VoiceChatStarted.Channel',
  'messageActionGroupCall.started_by': 'Chat.Service.VoiceChatStarted',
  'messageActionGroupCall.started_byYou': 'Chat.Service.VoiceChatStartedYou',
  'messageActionGroupCall.ended': 'Chat.Service.VoiceChatFinished.Channel',
  'messageActionGroupCall.ended_by': 'Chat.Service.VoiceChatFinished',
  'messageActionGroupCall.ended_byYou': 'Chat.Service.VoiceChatFinishedYou',

  'messageActionBotAllowed': 'Chat.Service.BotPermissionAllowed'
};

export type LangPackKey = /* string |  */keyof typeof lang | keyof typeof langSign;

export type FormatterArgument = string | number | Node | FormatterArgument[];
export type FormatterArguments = FormatterArgument[];

export const UNSUPPORTED_LANG_PACK_KEY: LangPackKey = IS_MOBILE ? 'Message.Unsupported.Mobile' : 'Message.Unsupported.Desktop';
const TEST_LOCAL = IS_BETA && true;

namespace I18n {
  export const strings: Map<LangPackKey, LangPackString> = new Map();
  export const countriesList: HelpCountry[] = [];
  let pluralRules: Intl.PluralRules;

  let cacheLangPackPromise: Promise<LangPackDifference>;
  let lastRequestedLangCode: string;
  let lastRequestedNormalizedLangCode: string;
  let lastAppliedLangCode: string;
  let timeFormat: State['settings']['timeFormat'];
  let isRTL = false;

  export function getLastRequestedLangCode() { return lastRequestedLangCode; }
  export function getLastRequestedNormalizedLangCode() { return lastRequestedNormalizedLangCode; }
  export function getLastAppliedLangCode() { return lastAppliedLangCode; }
  export function getTimeFormat() { return timeFormat; }
  export function getIsRTL() { return isRTL; }

  export const [langCodeNormalized, setLangCodeNormalized] = createSignal<TranslatableLanguageISO>();

  export function setRTL(rtl: boolean) {
    isRTL = rtl;
  }

  function setLangCode(langCode: string) {
    lastRequestedLangCode = langCode;
    lastRequestedNormalizedLangCode = langCode.split('-')[0];
    setLangCodeNormalized(lastRequestedNormalizedLangCode.split('-')[0] as any);
  }

  export function getCacheLangPack(dontLoadLocal?: boolean) {
    return Promise.all([
      commonStateStorage.get('langPack').then((langPack) => langPack || (dontLoadLocal ? undefined : loadLocalLangPack())),
      polyfillPromise
    ]).then(([langPack]) => langPack);
  }

  export function getCacheLangPackAndApply() {
    return cacheLangPackPromise ||= getCacheLangPack(true).then(async(langPack) => {
      if(!langPack) {
        langPack = await loadLocalLangPack();
        langPack = await saveLangPack(langPack, false);
      }

      setLangCode(langPack.lang_code);
      applyLangPack(langPack);
      return langPack;
    }).finally(() => {
      cacheLangPackPromise = undefined;
    });
  }

  export function setTimeFormat(
    format: State['settings']['timeFormat'],
    haveToUpdate = !!timeFormat && timeFormat !== format
  ) {
    if(timeFormat !== format) {
      // * drop formatters minted before the first call too — they carry no -u-hc- and would outlive it
      cachedDateTimeFormats.clear();
    }

    timeFormat = format;

    if(haveToUpdate) {
      const elements = Array.from(document.querySelectorAll(`.i18n`)) as HTMLElement[];
      elements.forEach((element) => {
        const instance = weakMap.get(element);

        if(instance instanceof IntlDateElement) {
          instance.update();
        }
      });
    }
  }

  export function loadLocalLangPack() {
    const defaultCode = App.langPackCode;
    return Promise.all([
      import('../lang'),
      import('../langSign'),
      import('../countries')
    ]).then(([lang, langSign, countries]) => {
      const strings: LangPackString[] = [];
      formatLocalStrings(lang.default, strings);
      formatLocalStrings(langSign.default, strings);

      const langPack: LangPackDifference = {
        _: 'langPackDifference',
        from_version: 0,
        lang_code: defaultCode,
        strings,
        version: App.langPackVersion,
        countries: countries.default,
        localVersion: App.langPackLocalVersion
      };
      return langPack;
    });
  }

  export function loadLangPack(langCode: string, web?: boolean, ignoreCache?: boolean) {
    web = true;
    const managers = rootScope.managers;
    return Promise.all([
      managers.appLangPackManager.getLangPack(langCode, web ? 'web' : App.langPack, ignoreCache),
      !web && managers.appLangPackManager.getLangPack(langCode, 'android', ignoreCache),
      import('../lang'),
      import('../langSign'),
      managers.appLangPackManager.getCountriesList(langCode, ignoreCache),
      polyfillPromise
    ]);
  }

  export function getStrings(langCode: string, strings: string[]) {
    return rootScope.managers.appLangPackManager.getStrings(langCode, strings);
  }

  export function formatLocalStrings(strings: any, pushTo: LangPackString[] = []) {
    for(const i in strings) {
      // @ts-ignore
      const v = strings[i];
      if(typeof(v) === 'string') {
        pushTo.push({
          _: 'langPackString',
          key: i,
          value: v
        });
      } else {
        pushTo.push({
          _: 'langPackStringPluralized',
          key: i,
          ...v
        });
      }
    }

    return pushTo;
  }

  export function getLangPackAndApply(langCode: string, web?: boolean, ignoreCache?: boolean) {
    const previousLangCode = lastRequestedLangCode;
    setLangCode(langCode);
    return loadLangPack(langCode, web, ignoreCache).catch((err) => {
      // * roll back so date formatters aren't built from a language that was never applied
      if(previousLangCode && lastRequestedLangCode === langCode) {
        setLangCode(previousLangCode);
      }

      throw err;
    }).then(([langPack1, langPack2, localLangPack1, localLangPack2, countries, _]) => {
      let strings: LangPackString[] = [];

      const pushLocal = () => [localLangPack1, localLangPack2].forEach((l) => {
        formatLocalStrings(l.default as any, strings);
      });

      // * local strings are English: let them win only over English, or a translation never shows
      const localWins = TEST_LOCAL && langCode === App.langPackCode;
      if(!localWins) pushLocal();
      strings = strings.concat(...[langPack1.strings, langPack2.strings].filter(Boolean));
      if(localWins) pushLocal();

      langPack1.strings = strings;
      langPack1.countries = countries;
      langPack1.localVersion = App.langPackLocalVersion;
      return saveLangPack(langPack1, true);
    });
  }

  export function saveLangPack(langPack: LangPackDifference, apply: boolean) {
    langPack.version ||= App.langPackVersion;

    if(!apply) return langPack;
    return commonStateStorage.set({langPack}).then(() => {
      applyLangPack(langPack);
      return langPack;
    });
  }

  export const polyfillPromise = (function checkIfPolyfillNeeded() {
    if(typeof(Intl) !== 'undefined' && typeof(Intl.PluralRules) !== 'undefined'/*  && false */) {
      return Promise.resolve();
    } else {
      return import('./pluralPolyfill').then((_Intl) => {
        (window as any).Intl = Object.assign(typeof(Intl) !== 'undefined' ? Intl : {}, _Intl.default);
      });
    }
  })();

  export function applyLangPack(langPack: LangPackDifference) {
    const currentLangCode = lastRequestedLangCode;
    if(langPack.lang_code !== currentLangCode) {
      return;
    }

    try {
      pluralRules = new Intl.PluralRules(lastRequestedNormalizedLangCode);
    } catch(err) {
      console.error('pluralRules error', err);
      pluralRules = new Intl.PluralRules(lastRequestedNormalizedLangCode.split('-', 1)[0]);
    }

    try {
      pluralRules = new Intl.PluralRules(langPack.lang_code);
    } catch(err) {
      console.error('pluralRules error', err);
      pluralRules = new Intl.PluralRules(langPack.lang_code.split('-', 1)[0]);
    }

    strings.clear();

    for(const string of langPack.strings) {
      strings.set(string.key as LangPackKey, string);
    }

    if(langPack.countries) {
      countriesList.length = 0;
      countriesList.push(...langPack.countries.countries);

      langPack.countries.countries.forEach((country) => {
        if(country.name) {
          const langPackKey: any = country.default_name;
          strings.set(langPackKey, {
            _: 'langPackString',
            key: langPackKey,
            value: country.name
          });
        }
      });
    }

    if(lastAppliedLangCode !== currentLangCode) {
      if(lastAppliedLangCode && rootScope.myId) {
        rootScope.managers.appReactionsManager.resetAvailableReactions();
        rootScope.managers.appUsersManager.indexMyself();
        rootScope.managers.dialogsStorage.indexMyDialog();
      }

      lastAppliedLangCode = currentLangCode;
      cachedDateTimeFormats.clear();
      rootScope.dispatchEvent('language_change', currentLangCode);
    }

    const elements = Array.from(document.querySelectorAll(`.i18n`)) as HTMLElement[];
    elements.forEach((element) => {
      const instance = weakMap.get(element);

      if(instance) {
        instance.update();
      }
    });

    rootScope.dispatchEventSingle('language_apply');
  }

  function pushNextArgument(out: ReturnType<typeof superFormatter>, args: FormatterArguments, indexHolder: {i: number}, i?: number) {
    const arg = args[i === undefined ? indexHolder.i++ : i];
    if(Array.isArray(arg)) {
      out.push(...arg as any);
    } else {
      out.push(arg);
    }
  }

  const IconMap: Record<string, Icon | (() => HTMLElement)> = {
    '>': 'next',
    '<': 'previous'
    // '⭐️': currencyStarIcon as () => HTMLElement
  };

  const iconsNoWhitespace = '><';
  const iconsKeys = Object.keys(IconMap);

  export function superFormatter(input: string, args?: FormatterArguments, indexHolder?: {i: number}): Exclude<FormatterArgument, FormatterArgument[]>[] {
    if(!indexHolder) { // set starting index for arguments without order
      indexHolder = {i: 0};
      const indexes = input.match(/(%|un)\d+/g);
      if(indexes?.length) {
        indexHolder.i = Math.max(...indexes.map((str) => +str.replace(/\D/g, '')));
      }
    }

    const out: ReturnType<typeof superFormatter> = [];
    const regExp = new RegExp(`(\\*\\*|__)(.+?)\\1|(\\n)|(\\[.+?\\]\\(.*?\\))|(?:^|\\s)(${iconsKeys.join('|')})(?:$|\\s)|un\\d|%\\d\\$.|%\\S`, 'g');

    let lastIndex = 0;
    input.replace(regExp, (match, p1: any, p2: any, p3: any, p4: string, p5: string, offset: number, string: string) => {
      // console.table({match, p1, p2, offset, string});

      if(offset > lastIndex) {
        out.push(string.slice(lastIndex, offset));
      }

      if(p1) {
        // offset += p1.length;
        let element: HTMLElement;
        switch(p1) {
          case '**': {
            element = document.createElement('b');
            break;
          }

          case '__': {
            element = document.createElement('i');
            break;
          }
        }

        element.append(...superFormatter(p2, args, indexHolder) as any);
        out.push(element);
      } else if(p3) {
        out.push(document.createElement('br'));
      } else if(p4) {
        const idx = p4.lastIndexOf(']');
        const text = p4.slice(1, idx);

        const url = p4.slice(idx + 2, p4.length - 1);
        let a: HTMLAnchorElement;
        if(url && matchUrlProtocol(url)) {
          a = document.createElement('a');
          const wrappedUrl = wrapUrl(url);
          a.href = wrappedUrl.url;
          if(wrappedUrl.onclick) a.setAttribute('onclick', wrappedUrl.onclick + '(this)');
          setBlankToAnchor(a);
        } else {
          a = args[indexHolder.i++] as HTMLAnchorElement;

          if(a instanceof DocumentFragment) { // right after wrapRichText
            a = a.firstChild as any;
          }

          // A string whose link carries its own placeholder ("[x](%1$@)", the shape the macOS
          // packs use) makes the index scan above start past the argument the anchor was passed
          // in, so there is nothing here to hang the link on. Render the label rather than
          // throwing: one malformed string used to take down everything around it.
          if(a && typeof(a) !== 'string') {
            a.textContent = ''; // reset content
          }
        }

        const formatted = superFormatter(text, args, indexHolder) as any;
        if(!a || typeof(a) === 'string') {
          out.push(...formatted);
        } else {
          a.append(...formatted);
          out.push(a);
        }
      } else if(p5) {
        const noWhitespace = iconsNoWhitespace.includes(p5);
        if(!noWhitespace && !match.startsWith(p5)) out.push(match[0]);
        const className = 'inline-icon';
        const i = IconMap[p5];
        if(typeof(i) === 'function') {
          const element = i();
          element.classList.add(className);
          out.push(element);
        } else {
          out.push(Icon(i, className));
        }
        if(!noWhitespace && match.startsWith(p5)) out.push(match[match.length - 1]);
      } else if(args) {
        const index = match.replace(/\D/g, '');
        pushNextArgument(
          out,
          args,
          indexHolder,
          !index || Number.isNaN(+index) ? undefined : Math.min(args.length - 1, +index - 1)
        );
      }

      lastIndex = offset + match.length;
      return '';
    });

    if(lastIndex !== input.length) {
      out.push(input.slice(lastIndex));
    }

    return out;
  }

  export function format<T extends boolean>(
    key: LangPackKey,
    plain?: T,
    args?: FormatterArguments
  ): T extends true ? string : ReturnType<typeof superFormatter> {
    const str = strings.get(key);
    let input: string;
    if(str) {
      if(str._ === 'langPackStringPluralized' && args?.length) {
        let v = args[0] as number | string;
        if(typeof(v) === 'string') v = +v.replace(/\D/g, '');
        const s = pluralRules.select(v);
        // @ts-ignore
        input = str[s + '_value'] || str['other_value'];
      } else if(str._ === 'langPackString') {
        input = str.value;
      } else {
        // input = '[' + key + ']';
        input = key;
      }
    } else {
      // input = '[' + key + ']';
      input = key;
    }

    const result = superFormatter(input, args);
    if(plain) { // * let's try a hack now... (don't want to replace []() entity)
      return result.map((item) => item instanceof HTMLBRElement ? '\n' : (item instanceof Node ? item.textContent : item)).join('') as any;
    } else {
      return result as any;
    }

    /* if(plain) {
      if(args?.length) {
        const regExp = /un\d|%\d\$.|%./g;
        let i = 0;
        input = input.replace(regExp, (match, offset, string) => {
          return '' + args[i++];
        });
      }

      return input;
    } else {
      return superFormatter(input, args);
    } */
  }

  export const weakMap: WeakMap<HTMLElement, IntlElementBase<IntlElementBaseOptions>> = new WeakMap();

  export type IntlElementBaseOptions = {
    element?: HTMLElement,
    property?: 'innerText' | 'innerHTML' | 'placeholder' | 'textContent',
  };

  abstract class IntlElementBase<Options extends IntlElementBaseOptions> {
    public element: IntlElementBaseOptions['element'];
    public property: IntlElementBaseOptions['property'];

    constructor(options?: Options) {
      this.element = options?.element || document.createElement('span');
      this.element.classList.add('i18n');

      this.property = options?.property;

      weakMap.set(this.element, this);
    }

    abstract update(options?: Options): void;
  }

  export type IntlElementOptions = IntlElementBaseOptions & {
    key?: LangPackKey,
    args?: FormatterArguments
  };
  export class IntlElement extends IntlElementBase<IntlElementOptions> {
    public key: IntlElementOptions['key'];
    public args: IntlElementOptions['args'];

    constructor(options: IntlElementOptions = {}) {
      super({...options, property: options.property ?? 'innerHTML'});

      if(options?.key) {
        this.update(options);
      }
    }

    public update(options?: IntlElementOptions) {
      safeAssign(this, options);

      if(!this.key) {
        this.element.replaceChildren();
        return;
      }

      if(this.property === 'innerHTML') {
        this.element.replaceChildren(...format(this.key, false, this.args) as any);
        if(this.args?.length) {
          this.element.normalize();
        }
      } else {
        // @ts-ignore
        const v = this.element[this.property];
        const formatted = format(this.key, true, this.args);

        // * hasOwnProperty won't work here
        if(v === undefined) this.element.dataset[this.property] = formatted;
        else (this.element as HTMLInputElement)[this.property] = formatted;
      }
    }

    public compareAndUpdateBool(options?: IntlElementOptions): boolean {
      if(this.key === options.key && deepEqual(this.args, options.args)) {
        return false;
      }

      this.update(options);
      return true;
    }

    public compareAndUpdate(options?: IntlElementOptions) {
      if(this.key === options.key && deepEqual(this.args, options.args)) {
        return;
      }

      return this.update(options);
    }
  }

  const cachedDateTimeFormats: Map<string, Intl.DateTimeFormat> = new Map();
  export function getDateTimeFormat(options: Intl.DateTimeFormatOptions = {}) {
    const json = JSON.stringify(options);
    let dateTimeFormat = cachedDateTimeFormats.get(json);
    if(!dateTimeFormat) {
      dateTimeFormat = new Intl.DateTimeFormat(lastRequestedNormalizedLangCode + (timeFormat ? '-u-hc-' + timeFormat : ''), options);
      cachedDateTimeFormats.set(json, dateTimeFormat);
    }

    return dateTimeFormat;
  }

  // * format() on a cached formatter is still ~20x the cost of string concatenation, and times repeat heavily —
  // * memoize per minute of day (max 1440 entries); keying by formatter makes the memo die with it on locale/format change
  const timeStringsCache: WeakMap<Intl.DateTimeFormat, Map<number, string>> = new WeakMap();
  const TIME_STRING_OPTIONS: Intl.DateTimeFormatOptions = {hour: '2-digit', minute: '2-digit'};
  const TIME_STRING_OPTIONS_JSON = JSON.stringify(TIME_STRING_OPTIONS);
  function warmTimeStrings(dateTimeFormat: Intl.DateTimeFormat, timeStrings: Map<number, string>) {
    // * a later locale/format change orphans this generation — drop the leftover work
    if(cachedDateTimeFormats.get(TIME_STRING_OPTIONS_JSON) !== dateTimeFormat) {
      return;
    }

    const date = new Date();
    let budget = 240; // * ~0.2ms per chunk, the rest resumes from the next idle period
    for(let minutesKey = 0; minutesKey < 1440; ++minutesKey) {
      if(timeStrings.has(minutesKey)) {
        continue;
      }

      if(--budget < 0) {
        scheduleWarmTimeStrings(dateTimeFormat, timeStrings);
        return;
      }

      const hours = (minutesKey / 60) | 0, minutes = minutesKey % 60;
      date.setHours(hours, minutes, 0, 0);
      // * a DST hole would shift the synthetic date to a different wall time — leave such keys to the lazy path
      if(date.getHours() === hours && date.getMinutes() === minutes) {
        timeStrings.set(minutesKey, dateTimeFormat.format(date));
      }
    }
  }

  function scheduleWarmTimeStrings(dateTimeFormat: Intl.DateTimeFormat, timeStrings: Map<number, string>) {
    const warm = () => warmTimeStrings(dateTimeFormat, timeStrings);
    self.requestIdleCallback ? self.requestIdleCallback(warm) : setTimeout(warm, 100);
  }

  function formatTimeString(date: Date) {
    // * skip getDateTimeFormat's per-call JSON.stringify on this hot path
    const dateTimeFormat = cachedDateTimeFormats.get(TIME_STRING_OPTIONS_JSON) || getDateTimeFormat(TIME_STRING_OPTIONS);
    let timeStrings = timeStringsCache.get(dateTimeFormat);
    if(!timeStrings) {
      timeStringsCache.set(dateTimeFormat, timeStrings = new Map());
      // * pre-render the whole day off the critical path so batches of distinct times never pay for Intl
      scheduleWarmTimeStrings(dateTimeFormat, timeStrings);
    }

    const minutesKey = date.getHours() * 60 + date.getMinutes();
    let text = timeStrings.get(minutesKey);
    if(text === undefined) {
      timeStrings.set(minutesKey, text = dateTimeFormat.format(date));
    }

    return text;
  }

  export type IntlDateElementOptions = IntlElementBaseOptions & {
    date?: Date,
    options: Intl.DateTimeFormatOptions
  };
  export class IntlDateElement extends IntlElementBase<IntlDateElementOptions> {
    public date: IntlDateElementOptions['date'];
    public options: IntlDateElementOptions['options'];

    constructor(options: IntlDateElementOptions) {
      super({...options, property: options.property ?? 'textContent'});
      setDirection(this.element);

      if(options?.date) {
        this.update(options);
      }
    }

    public update(options?: IntlDateElementOptions) {
      safeAssign(this, options);

      let text: string;
      if(this.options.hour && this.options.minute && Object.keys(this.options).length === 2/*  && false */) {
        // * no capitalizeFirstLetter here — hu renders 'de. 12:05' and must not become 'De. 12:05'
        text = formatTimeString(this.date);
      } else {
        // * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Locale/hourCycle#adding_an_hour_cycle_via_the_locale_string
        const dateTimeFormat = getDateTimeFormat(this.options);
        text = capitalizeFirstLetter(dateTimeFormat.format(this.date));
      }

      (this.element as any)[this.property] = text;
    }
  }

  export function i18n(key: LangPackKey, args?: FormatterArguments) {
    return new IntlElement({key, args}).element;
  }

  export function i18n_(options: IntlElementOptions) {
    return new IntlElement(options).element;
  }

  export function _i18n(element: HTMLElement, key: LangPackKey, args?: FormatterArguments, property?: IntlElementOptions['property']) {
    return new IntlElement({element, key, args, property}).element;
  }
}

export {I18n};
export default I18n;

const i18n = I18n.i18n;
export {i18n};

const i18n_ = I18n.i18n_;
export {i18n_};

const _i18n = I18n._i18n;
export {_i18n};

export function joinElementsWith<T>(
  elements: T[],
  joiner: T | string | ((isLast: boolean) => T)
): T[] {
  const arr = elements.slice(0, 1) as T[];
  for(let i = 1; i < elements.length; ++i) {
    const isLast = (elements.length - 1) === i;
    arr.push(typeof(joiner) === 'function' ? (joiner as any)(isLast) : joiner);
    arr.push(elements[i]);
  }

  return arr;
}


export function join(elements: (Node | string)[], useLast: boolean, plain: true): string;
export function join(elements: (Node | string)[], useLast?: boolean, plain?: false): (string | Node)[];
export function join(elements: (Node | string)[], useLast: boolean, plain: boolean): string | (string | Node)[];
export function join(elements: (Node | string)[], useLast = true, plain?: boolean): string | (string | Node)[] {
  const joined = joinElementsWith(elements, (isLast) => {
    const langPackKey: LangPackKey = isLast && useLast ? 'AutoDownloadSettings.LastDelimeter' : 'AutoDownloadSettings.Delimeter';
    return plain ? I18n.format(langPackKey, true) : i18n(langPackKey);
  });

  return plain ? joined.join('') : joined;
}

export async function handleUpdateLangPack(update: {difference: LangPackDifference}) {
  const {difference} = update;

  // Check if this update is for the current language
  if(difference.lang_code !== I18n.getLastRequestedLangCode()) {
    return;
  }

  // Get current langPack from storage
  const storedLangPack = await I18n.getCacheLangPack();
  if(storedLangPack?.lang_code !== difference.lang_code || storedLangPack.lang_code !== I18n.getLastRequestedLangCode()) {
    return;
  }

  if(storedLangPack.version !== difference.from_version) {
    handleUpdateLangPackTooLong(difference);
    return;
  }

  // Apply updates to langPack
  if(difference.strings) {
    const storedStrings = storedLangPack.strings ||= [];
    for(const string of difference.strings) {
      const existingIndex = storedStrings.findIndex((s) => s.key === string.key);
      if(existingIndex !== -1) {
        storedStrings[existingIndex] = string;
      } else {
        storedStrings.push(string);
      }
    }
  }

  // if(difference.countries) {
  //   const storedCountries = storedLangPack.countries ||= {_: 'help.countriesList', countries: [], hash: 0};
  //   for(const country of difference.countries.countries) {
  //     const existingIndex = storedCountries.countries.findIndex((c) => c.default_name === country.default_name);
  //     if(existingIndex !== -1) {
  //       storedCountries.countries[existingIndex] = country;
  //     } else {
  //       storedCountries.countries.push(country);
  //     }
  //   }
  //   // Update hash if provided
  //   if(difference.countries.hash) {
  //     storedCountries.hash = difference.countries.hash;
  //   }
  // }

  // Update version
  storedLangPack.version = difference.version;
  storedLangPack.from_version = difference.from_version;

  // Save updated langPack and apply it
  await I18n.saveLangPack(storedLangPack, true);
}

export function handleUpdateLangPackTooLong(update: {lang_code: string}) {
  const {lang_code} = update;

  // Check if this update is for the current language
  if(lang_code !== I18n.getLastRequestedLangCode()) {
    return;
  }

  // I18n.getLangPack(lang_code, undefined, true);
  checkLangPackForUpdates();
}

export function handleStateCleared() {
  handleUpdateLangPackTooLong({lang_code: I18n.getLastRequestedLangCode()});
}

export async function checkLangPackForUpdates() {
  const storedLangPack = await I18n.getCacheLangPack();
  const difference = await rootScope.managers.appLangPackManager.getDifference(storedLangPack.lang_code, storedLangPack.version);
  if(difference.version > storedLangPack.version) {
    return handleUpdateLangPack({difference});
  }
}

// Listen for events from rootScope to handle server updates
rootScope.addEventListener('langpack_update', handleUpdateLangPack);
rootScope.addEventListener('langpack_update_too_long', handleUpdateLangPackTooLong);
rootScope.addEventListener('state_cleared', handleStateCleared);

MOUNT_CLASS_TO && (MOUNT_CLASS_TO.I18n = I18n);
